import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { CommentEntity } from '@interpals/api-web';
import i18next from 'i18next';

import api from 'api';
import { ReduxState } from 'redux/root-reducer';
import { createCustomToast } from 'services/toasts';
import { PHOTO_COMMENTS_LIMIT, POST_COMMENTS_LIMIT, REPLIES_LIMIT } from 'constants/limits';
import { localeCapitalize } from 'utils/i18n';

interface CreateCommentParams {
    entity_type: 'photo' | 'post';
    entity_id: string;
    message: string;
    reply_to?: string;
}

const calculateOffset = (comments: { [key: string]: CommentEntity }, entity_id: string): number => {
    let commentsRelatedToEntityCount = 0;

    for (const key in comments) {
        if (comments[key].entity_id === entity_id) {
            commentsRelatedToEntityCount += 1;
        }
    }

    return commentsRelatedToEntityCount;
};

export const getPhotoComments = createAsyncThunk<CommentEntity[], string, { state: ReduxState }>(
    'get/photo-comments',
    async (id, { getState }) => {
        const comments = getState().comments;
        const offset = calculateOffset(comments, id);

        return await api
            .getPhotoComments({ id, nest_replies: true, offset, limit: PHOTO_COMMENTS_LIMIT })
            .then(({ data }) => data);
    },
);

export const getPostComments = createAsyncThunk<
    CommentEntity[],
    { id: string; feed_owner_id?: string },
    { state: ReduxState }
>('get/post-comments', async ({ id, feed_owner_id }, { getState }) => {
    const comments = getState().comments;
    const offset = calculateOffset(comments, id);

    return await api
        .getPostComments({ id, feed_owner_id, nest_replies: true, offset, limit: POST_COMMENTS_LIMIT })
        .then(({ data }) => data);
});

export const getCommentReplies = createAsyncThunk<
    CommentEntity[],
    { id: string; entity_id: string },
    { state: ReduxState }
>('get/comment-replies', async ({ id, entity_id }, { getState }) => {
    const offset = getState().comments[id].replies?.length ?? 0;

    return await api.getCommentReplies({ id, entity_id, offset, limit: REPLIES_LIMIT }).then(({ data }) => data);
});

export const createComment = createAsyncThunk<CommentEntity, CreateCommentParams>(
    'create/comment',
    async (params, { dispatch }) => {
        return await api.createComment({ ...params }).then((response) => {
            dispatch(updatePostCommentsCount({ entityId: params.entity_id, actionType: 'add' }));

            return response;
        });
    },
);

interface DeleteCommentParams {
    id: string;
    entityId: string;
    replyToCid?: string;
}

interface DeleteCommentResponse {
    id: string;
    replyToCid?: string;
}

export const deleteComment = createAsyncThunk<DeleteCommentResponse, DeleteCommentParams>(
    'delete/comment',
    async (params, { dispatch }) => {
        const { id, entityId, replyToCid } = params;

        return await api.deleteComment({ id }).then(() => {
            dispatch(updatePostCommentsCount({ entityId, actionType: 'delete' }));
            createCustomToast(
                'success',
                `${i18next.t('Common.SuccessEntityDeletionCaption', {
                    entity: localeCapitalize(i18next.t('Common.Entity.Comment')),
                })}`,
            );

            return {
                id,
                replyToCid,
            };
        });
    },
);

type UpdatePostCommentCountParams = { entityId: string; actionType: 'add' | 'delete' };

export const updatePostCommentsCount = createAction<UpdatePostCommentCountParams>('update/post-comments-count');
export const moderationDeleteComment = createAction<DeleteCommentParams>('delete/moderation/comment');
